import {
  Button,
  CircularProgress,
  Radio,
  ModalTitle,
  TimePicker,
  Card,
  TextField,
} from "@cambridgeassessment/cambridge-ui";
import { isEmpty } from "lodash";
import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";
import "./Modal.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isFirstDateGreaterThanSecond ,lockScrollPosition, unlockScrollPosition, transformResponseToBST} from "../constants";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { setAlert } from "../redux/slice/GlobalMessage";
import { updateSeriesResultEmbargoDates } from "../services/muleSoftServices";
import { SectionSpinner } from "./Spinner";

export const DownloadModal = ({ open, close }) => {
  return (
    <Modal show={open} onHide={close} centered size="md">
      <Modal.Header className="border-0" closeButton></Modal.Header>
      <Modal.Body>
        <div className="text-sm-center">
          <h4>
            <span className="fw">We are generating your report</span>
          </h4>
        </div>
        <div className="mt-4 text-sm-center">
          <h6>
            <span className="fw">This could take a few minutes.</span>
          </h6>
        </div>
        <div className="mt-4 text-sm-center">
          <h6 className="fs">
            You can close this message and your report will
          </h6>
        </div>
        <div className="text-sm-center">
          <h6 className="fs">download automatically.</h6>
        </div>
        <div className="mt-4 text-sm-center">
          <CircularProgress />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export const CentreSelectionModal = ({ open, close, orgs }) => {
  const [selectedCentre, setSelectedCentre] = useState({}),
    [orgId, setOrgId] = useState("");
  return (
    <Modal show={open} centered scrollable={true} size="lg">
      <Modal.Body>
        <p className="mb-2 text-start fw-semibold">Select your centre</p>
        <p className="text-start fs">
          <strong>
            Because you're associated with multiple centres you need to select
            one first.{" "}
          </strong>
          Later you will be able to switch between centres by clicking on the
          Select another centre link on the home page.
        </p>
        <div style={{ maxHeight: "170px", overflowY: "auto" }}>
          {orgs.map((c, i) => {
            return (
              <div key={i} style={{ marginLeft: "2px" }}>
                <Radio
                  fs
                  name="radio"
                  checked={c?.sourceSystems[0]?.orgId === orgId}
                  label={`${c?.sourceSystems[0]?.orgId}` + " - " + `${c?.name}`}
                  onChange={() => {
                    setSelectedCentre(orgs[i]);
                    setOrgId(c?.sourceSystems[0]?.orgId);
                  }}
                />
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={isEmpty(selectedCentre)}
          onClick={() => close([selectedCentre])}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const SeriesEmbargoModal = ({
  open,
  close,
  seriesEmbargoDate,
  seriesTimePicker,
  dispatch,
  resultRelease,
  setResultRelease,
  selectedSeries,
  embargoState,
}) => {
  const [seriesError, setSeriesError] = useState(null);
  const [loading,setLoading] = useState(false);
  const observer = useRef(null);
  const scrollYRef = useRef(0);
  
  const {
    roleFromSAP,
    totalLoggedInData: { access_token },
    orgs = {},
  } = useSelector(state => state.userInfo || {});
  const reduxDispatch = useDispatch();

  useEffect(() => {
    observer.current = new MutationObserver(() => {
      const paperDivs = document.querySelectorAll(
        "div.MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation1"
      );
      const targetDiv = Array.from(paperDivs).find(div =>
        Array.from(div.classList).some(c => c.endsWith("-paper"))
      );
      console.log(targetDiv);
      if (targetDiv) {
        targetDiv.style.setProperty("overflow", "auto", "important");
        targetDiv.style.setProperty("height", "200px", "important");
      }
    });
    observer.current.observe(document.body, { childList: true, subtree: true });
    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, []);

  useEffect(() => {
    if (open) {
      scrollYRef.current = lockScrollPosition();
    } else {
      unlockScrollPosition(scrollYRef.current);
    }
    return () => {
      unlockScrollPosition(scrollYRef.current);
    };
  }, [open]);

  useEffect(() => {
    if (seriesError) {
      const timer = setTimeout(() => {
        setSeriesError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [seriesError]);

  const seriesValidationHandler = async () => {
    const selectedDate = `${seriesEmbargoDate.format(
      "DD-MM-YYYY"
    )} ${seriesTimePicker}`;
    const previousDate = `${resultRelease}`;
    const isFormInvalid = isFirstDateGreaterThanSecond(
      selectedDate,
      previousDate
    );
    if (!isFormInvalid) {
      setSeriesError(
        `Selected date and time should be greater than the CI Direct Series result release date ${resultRelease}`
      );
    } else {
      try {
        setLoading(true)
        const response = await updateSeriesResultEmbargoDates({
          ...embargoState,
          series: selectedSeries,
          roleName: roleFromSAP,
          token: access_token,
          submissionType: "series",
          bpId: orgs[0]?.bpid,
        });
        if (response.error) {
          reduxDispatch(
            setAlert({
              message: `${response?.message} error while processing the data`,
              status: "error",
            })
          );
        }
        if (response?.data?.status === "error") {
          reduxDispatch(
            setAlert({ message: `${response.data.message}`, status: "error" })
          );
        }
        if (response?.data?.status === "success") {
          reduxDispatch(
            setAlert({ message: `${response.data.message}`, status: "success" })
          );
          dispatch({
            type: "RESET_ALL",
          });
          const bstResponseCI = transformResponseToBST(
            response?.data?.result_release_date
          );
          const bstResponseRM = transformResponseToBST(
            response?.data?.result_release_date_rm
          );
          const formattedCI = `${bstResponseCI?.date} ${bstResponseCI?.time} ${bstResponseCI?.format}`;
          const formattedRM = `${bstResponseRM?.date} ${bstResponseRM?.time} ${bstResponseRM?.format}`;

          setResultRelease({
            ci_result_release_date: formattedCI,
            rm_result_release_date: formattedRM,
          });

          dispatch({
            type: "SET_SERIES_DATA",
            payload: {
              ...bstResponseRM,
              initialLoad: true,
            },
          });
        }
      } catch (error) {
        console.log(`Exception while processing: ${error}`);
      } finally{
        close();
        setLoading(false)
      }
    }
  };

  return (
    <Modal
      show={open}
      onClose={close}
      scrollable={true}
      size="lg"
      className="modelZindex"
      style={{ marginTop: "4%" }}>
      <ModalTitle onClose={close}>Override Embargo at series level</ModalTitle>
      {loading && <SectionSpinner />}
      <Modal.Body>
        <div>
          <p
            style={{
              fontWeight: "bold",
              background: "yellow",
            }}>{`If you change the series-level date, it will be applied across all qualifications and centers for the series ${selectedSeries}.`}</p>
          {seriesError && (
            <p style={{ color: "red" }}>
              <strong>{seriesError} </strong>
            </p>
          )}
          <Card className="mb-4" style={{ padding: "10px 24px" }}>
            <div className="mb-4 row">
              <div className="col-lg-5">
                <div className="customDatePicker">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="Result release date"
                      value={seriesEmbargoDate}
                      onChange={newValue =>
                        dispatch({
                          type: "SET_SERIES_DATA",
                          payload: {
                            date: newValue ? newValue.format("DD-MM-YYYY") : "",
                          },
                        })
                      }
                      inputFormat="DD-MM-YYYY"
                      slotProps={{
                        popper: {
                          disablePortal: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="ModalcustomTimePicker">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <TimePicker
                      id="modal_time_picker"
                      label="Time (UK)"
                      value={seriesTimePicker}
                      onChange={newValue =>
                        dispatch({
                          type: "SET_SERIES_DATA",
                          payload: {
                            time: newValue,
                          },
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>
                </div>
              </div>
              <div className="col-lg-1">
                <Button
                  className="btnQualification"
                  variant="outlined"
                  onClick={seriesValidationHandler}
                  disabled={Object.values({
                    seriesEmbargoDate,
                    seriesTimePicker,
                  }).some(value => value === "" || value === null)}>
                  Add
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Modal.Body>
    </Modal>
  );
};